<template>
  <b-row>
    <b-col cols="12">
      <!-- Loading indicator -->
      <b-row v-if="loading" class="py-3">
        <b-col cols="12" class="text-center">
          <b-spinner small variant="primary"></b-spinner>
          <span class="ml-2 text-xs text-gray">Loading beneficial owners...</span>
        </b-col>
      </b-row>

      <!-- Empty state -->
      <b-row v-else-if="persons.length === 0 && addedOwners.length === 0 && removedOwners.length === 0 && changedList.length === 0" class="py-3">
        <b-col cols="12" class="text-center">
          <span class="text-xs text-gray">No beneficial owners data found.</span>
        </b-col>
      </b-row>

      <template v-else>
        <!-- Particulars Changed Section -->
        <b-row class="mt-2" v-if="changedList.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal text-gray" style="border: solid 1px #dedede;">
              <thead>
                <tr class="bg-green-pale">
                  <td colspan="12" class="px-2 py-2 font-bold">Particulars Changed</td>
                </tr>
              </thead>
            </table>
            <table cellspacing="0" cellpadding="0" style="width: 100%; margin-bottom: 12px; border-collapse: collapse;">
              <thead>
                <tr style="text-align: center; font-weight: bold; background-color: #f8f9fa;">
                  <td style="border: solid 1px #dedede; padding: 6px; width: 25%;">Name</td>
                  <td style="border: solid 1px #dedede; padding: 6px; width: 75%;">Change Effected (Field / Old vs New)</td>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in changedList" :key="idx">
                  <td style="border: solid 1px #dedede; padding: 6px; font-weight: 600; vertical-align: top;">
                    {{ item.name }}
                  </td>
                  <td style="border: solid 1px #dedede; padding: 4px;">
                    <table cellspacing="0" cellpadding="0" style="width: 100%; border-collapse: collapse;">
                      <thead>
                        <tr style="font-weight: 600; background-color: #f1f3f5; font-size: 11px;">
                          <td style="border: solid 1px #dedede; padding: 4px; width: 5%;">#</td>
                          <td style="border: solid 1px #dedede; padding: 4px; width: 30%;">Change Type</td>
                          <td style="border: solid 1px #dedede; padding: 4px; width: 32%;">Old Value</td>
                          <td style="border: solid 1px #dedede; padding: 4px; width: 33%;">New Value</td>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(chg, chgIdx) in item.changes" :key="chgIdx">
                          <td style="border: solid 1px #dedede; padding: 4px;">{{ chgIdx + 1 }}</td>
                          <td style="border: solid 1px #dedede; padding: 4px; font-weight: 600;">{{ chg.type }}</td>
                          <td style="border: solid 1px #dedede; padding: 4px; color: #dc3545;">{{ chg.from }}</td>
                          <td style="border: solid 1px #dedede; padding: 4px; color: #28a745;">{{ chg.to }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

        <!-- Added Beneficial Owners Section -->
        <b-row class="mt-2" v-if="addedOwners.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal text-gray mb-2" style="border: solid 1px #dedede;">
              <thead>
                <tr class="bg-green-pale">
                  <td colspan="12" class="px-2 py-2 font-bold">Added Beneficial Owners</td>
                </tr>
              </thead>
            </table>
            <b-row class="mx-1 py-1" v-for="(person, idx) in addedOwners" :key="'added-' + idx">
              <b-col cols="12">
                <b-row class="py-1 border-b border-gray">
                  <b-col cols="6">
                    <span class="text-xs font-bold text-capitalize">
                      {{ person.givenName }} {{ person.middleName }} {{ person.surName }}
                    </span>
                    <b-badge variant="success" class="ml-2 text-xs">Added</b-badge>
                  </b-col>
                  <b-col cols="4" v-if="person.parentBrn">
                    <span class="text-xs font-bold text-capitalize mr-2">Parent:</span>
                    <span class="text-xs font-bold text-capitalize">{{ person.parentBrn }}</span>
                    <span class="text-xs font-bold text-capitalize ml-1">{{ person.parentName }}</span>
                  </b-col>
                  <b-col :cols="person.parentBrn ? 2 : 6">
                    <button class="bg-blue text-white rounded-sm px-2 py-1 text-xs float-end" @click="toggleShow('added', idx)">
                      <span v-if="selectedCategory === 'added' && selectedIdx === idx">Hide Details</span>
                      <span v-else>Show Details</span>
                    </button>
                  </b-col>
                </b-row>
                <b-row v-if="showDetails && selectedCategory === 'added' && selectedIdx === idx">
                  <b-col cols="12">
                    <b-row class="mt-2">
                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Given Name">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.givenName"></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="person.middleName" class="text-sm my-1" label="Middle Name">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.middleName"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Surname">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.surName"></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="person.idType" class="text-sm my-1" label="ID Type">
                          <b-form-input class="form-text text-sm noFocus text-capitalize" type="text" disabled :value="person.idType ? person.idType.toLowerCase() : ''"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="ID Number">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.idNumber"></b-form-input>
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Nationality">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.nationality"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Email Address">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.email"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Phone Number">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.phoneNumber"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Gender">
                          <b-form-input class="form-text text-sm noFocus text-capitalize" type="text" disabled :value="person.sex ? person.sex.toLowerCase() : ''"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Date of Birth">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="dateFormat(person.dateOfBirth)"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Occupation">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.occupation"></b-form-input>
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Physical Address">
                          <b-form-textarea v-if="person.village" class="form-text text-sm noFocus" disabled rows="3"
                            :value="(person.village.region ? person.village.region.name + ' > ' : '') + person.village.district.name + ' > ' + person.village.subcounty.name + ' > ' + person.village.parish.name + ' > ' + person.village.village.name">
                          </b-form-textarea>
                          <b-form-textarea v-else class="form-text text-sm noFocus" disabled rows="3" :value="person.foreignPhysicalAddress">
                          </b-form-textarea>
                        </b-form-group>
                        <b-row class="mt-2">
                          <b-col cols="12">
                            <b-form-group class="text-sm my-1" label="Postal Address">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.postalAddress"></b-form-input>
                              <PostalAddressValidator v-if="person.postalAddress" :box-number="person.postalAddress"/>
                            </b-form-group>
                            <b-form-group class="text-sm my-1" label="Street">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.street"></b-form-input>
                            </b-form-group>
                            <b-form-group class="text-sm my-1" label="Plot">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.plot"></b-form-input>
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                    <b-row class="mt-2 pt-2 border-t border-gray">
                      <b-col cols="12">
                        <span class="text-xs font-bold text-gray">Nature of Control:</span>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group v-if="person.parentBrn" class="text-sm my-1" label="Parent BRN">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.parentBrn"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Shares / Control">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.percentageShares"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Percentage Voting Rights">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.percentageVotingRights"></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Has Voting Rights">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.hasVotingRights"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Has Right To Appoint">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.hasRightToAppoint"></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group v-if="person.amountGuaranteed" class="text-sm my-1" label="Amount Guaranteed">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.amountGuaranteed"></b-form-input>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <!-- Removed Beneficial Owners Section -->
        <b-row class="mt-2" v-if="removedOwners.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal text-gray mb-2" style="border: solid 1px #dedede;">
              <thead>
                <tr class="bg-red-pale">
                  <td colspan="12" class="px-2 py-2 font-bold">Removed Beneficial Owners</td>
                </tr>
              </thead>
            </table>
            <b-row class="mx-1 py-1" v-for="(person, idx) in removedOwners" :key="'removed-' + idx">
              <b-col cols="12">
                <b-row class="py-1 border-b border-gray">
                  <b-col cols="6">
                    <span class="text-xs font-bold text-capitalize">
                      {{ person.givenName }} {{ person.middleName }} {{ person.surName }}
                    </span>
                    <b-badge variant="danger" class="ml-2 text-xs">Removed</b-badge>
                  </b-col>
                  <b-col cols="4" v-if="person.parentBrn">
                    <span class="text-xs font-bold text-capitalize mr-2">Parent:</span>
                    <span class="text-xs font-bold text-capitalize">{{ person.parentBrn }}</span>
                    <span class="text-xs font-bold text-capitalize ml-1">{{ person.parentName }}</span>
                  </b-col>
                  <b-col :cols="person.parentBrn ? 2 : 6">
                    <button class="bg-blue text-white rounded-sm px-2 py-1 text-xs float-end" @click="toggleShow('removed', idx)">
                      <span v-if="selectedCategory === 'removed' && selectedIdx === idx">Hide Details</span>
                      <span v-else>Show Details</span>
                    </button>
                  </b-col>
                </b-row>
                <b-row v-if="showDetails && selectedCategory === 'removed' && selectedIdx === idx">
                  <b-col cols="12">
                    <b-row class="mt-2">
                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Given Name">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.givenName"></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="person.middleName" class="text-sm my-1" label="Middle Name">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.middleName"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Surname">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.surName"></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="person.idType" class="text-sm my-1" label="ID Type">
                          <b-form-input class="form-text text-sm noFocus text-capitalize" type="text" disabled :value="person.idType ? person.idType.toLowerCase() : ''"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="ID Number">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.idNumber"></b-form-input>
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Nationality">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.nationality"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Email Address">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.email"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Phone Number">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.phoneNumber"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Gender">
                          <b-form-input class="form-text text-sm noFocus text-capitalize" type="text" disabled :value="person.sex ? person.sex.toLowerCase() : ''"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Date of Birth">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="dateFormat(person.dateOfBirth)"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Occupation">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.occupation"></b-form-input>
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Physical Address">
                          <b-form-textarea v-if="person.village" class="form-text text-sm noFocus" disabled rows="3"
                            :value="(person.village.region ? person.village.region.name + ' > ' : '') + person.village.district.name + ' > ' + person.village.subcounty.name + ' > ' + person.village.parish.name + ' > ' + person.village.village.name">
                          </b-form-textarea>
                          <b-form-textarea v-else class="form-text text-sm noFocus" disabled rows="3" :value="person.foreignPhysicalAddress">
                          </b-form-textarea>
                        </b-form-group>
                        <b-row class="mt-2">
                          <b-col cols="12">
                            <b-form-group class="text-sm my-1" label="Postal Address">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.postalAddress"></b-form-input>
                              <PostalAddressValidator v-if="person.postalAddress" :box-number="person.postalAddress"/>
                            </b-form-group>
                            <b-form-group class="text-sm my-1" label="Street">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.street"></b-form-input>
                            </b-form-group>
                            <b-form-group class="text-sm my-1" label="Plot">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.plot"></b-form-input>
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                    <b-row class="mt-2 pt-2 border-t border-gray">
                      <b-col cols="12">
                        <span class="text-xs font-bold text-gray">Nature of Control:</span>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group v-if="person.parentBrn" class="text-sm my-1" label="Parent BRN">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.parentBrn"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Shares / Control">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.percentageShares"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Percentage Voting Rights">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.percentageVotingRights"></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Has Voting Rights">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.hasVotingRights"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Has Right To Appoint">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.hasRightToAppoint"></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group v-if="person.amountGuaranteed" class="text-sm my-1" label="Amount Guaranteed">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.amountGuaranteed"></b-form-input>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <!-- Current Beneficial Owners Section -->
        <b-row class="mt-2" v-if="persons.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal text-gray mb-2" style="border: solid 1px #dedede;">
              <thead>
                <tr class="bg-gray-pale">
                  <td colspan="12" class="px-2 py-2 font-bold">Current Beneficial Owners</td>
                </tr>
              </thead>
            </table>
            <b-row class="mx-1 py-1" v-for="(person, idx) in persons" :key="'current-' + idx">
              <b-col cols="12">
                <b-row class="py-1 border-b border-gray">
                  <b-col cols="6">
                    <span class="text-xs font-bold text-capitalize">
                      {{ person.givenName }} {{ person.middleName }} {{ person.surName }}
                    </span>
                  </b-col>
                  <b-col cols="4" v-if="person.parentBrn">
                    <span class="text-xs font-bold text-capitalize mr-2">Parent:</span>
                    <span class="text-xs font-bold text-capitalize">{{ person.parentBrn }}</span>
                    <span class="text-xs font-bold text-capitalize ml-1">{{ person.parentName }}</span>
                  </b-col>
                  <b-col :cols="person.parentBrn ? 2 : 6">
                    <button class="bg-blue text-white rounded-sm px-2 py-1 text-xs float-end" @click="toggleShow('current', idx)">
                      <span v-if="selectedCategory === 'current' && selectedIdx === idx">Hide Details</span>
                      <span v-else>Show Details</span>
                    </button>
                  </b-col>
                </b-row>
                <b-row v-if="showDetails && selectedCategory === 'current' && selectedIdx === idx">
                  <b-col cols="12">
                    <b-row class="mt-2">
                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Given Name">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.givenName"></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="person.middleName" class="text-sm my-1" label="Middle Name">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.middleName"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Surname">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.surName"></b-form-input>
                        </b-form-group>
                        <b-form-group v-if="person.idType" class="text-sm my-1" label="ID Type">
                          <b-form-input class="form-text text-sm noFocus text-capitalize" type="text" disabled :value="person.idType ? person.idType.toLowerCase() : ''"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="ID Number">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.idNumber"></b-form-input>
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Nationality">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.nationality"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Email Address">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.email"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Phone Number">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.phoneNumber"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Gender">
                          <b-form-input class="form-text text-sm noFocus text-capitalize" type="text" disabled :value="person.sex ? person.sex.toLowerCase() : ''"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Date of Birth">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="dateFormat(person.dateOfBirth)"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Occupation">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.occupation"></b-form-input>
                        </b-form-group>
                      </b-col>

                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Physical Address">
                          <b-form-textarea v-if="person.village" class="form-text text-sm noFocus" disabled rows="3"
                            :value="(person.village.region ? person.village.region.name + ' > ' : '') + person.village.district.name + ' > ' + person.village.subcounty.name + ' > ' + person.village.parish.name + ' > ' + person.village.village.name">
                          </b-form-textarea>
                          <b-form-textarea v-else class="form-text text-sm noFocus" disabled rows="3" :value="person.foreignPhysicalAddress">
                          </b-form-textarea>
                        </b-form-group>
                        <b-row class="mt-2">
                          <b-col cols="12">
                            <b-form-group class="text-sm my-1" label="Postal Address">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.postalAddress"></b-form-input>
                              <PostalAddressValidator v-if="person.postalAddress" :box-number="person.postalAddress"/>
                            </b-form-group>
                            <b-form-group class="text-sm my-1" label="Street">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.street"></b-form-input>
                            </b-form-group>
                            <b-form-group class="text-sm my-1" label="Plot">
                              <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.plot"></b-form-input>
                            </b-form-group>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                    <b-row class="mt-2 pt-2 border-t border-gray">
                      <b-col cols="12">
                        <span class="text-xs font-bold text-gray">Nature of Control:</span>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group v-if="person.parentBrn" class="text-sm my-1" label="Parent BRN">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.parentBrn"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Shares / Control">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.percentageShares"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Percentage Voting Rights">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.percentageVotingRights"></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group class="text-sm my-1" label="Has Voting Rights">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.hasVotingRights"></b-form-input>
                        </b-form-group>
                        <b-form-group class="text-sm my-1" label="Has Right To Appoint">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.hasRightToAppoint"></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="4">
                        <b-form-group v-if="person.amountGuaranteed" class="text-sm my-1" label="Amount Guaranteed">
                          <b-form-input class="form-text text-sm noFocus" type="text" disabled :value="person.amountGuaranteed"></b-form-input>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </template>
    </b-col>
  </b-row>
</template>

<script>
import moment from "moment";
import { api } from "@/app/services/api.service";
import PostalAddressValidator from "@/views/home/commons/PostalAddressValidator.vue";

export default {
  name: "CFBeneficialOwnersV2",
  components: {
    PostalAddressValidator
  },
  props: {
    trackingNumber: {
      type: String,
      default: ""
    },
    taskData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      selectedIdx: null,
      selectedCategory: "current",
      showDetails: false,
      persons: [],
      addedOwners: [],
      removedOwners: [],
      changedList: []
    };
  },
  watch: {
    trackingNumber: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadData(newVal);
        }
      }
    },
    taskData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal.trackingNo && !this.trackingNumber) {
          this.loadData(newVal.trackingNo);
        } else if (newVal) {
          this.processTaskDataChanges(newVal);
        }
      }
    }
  },
  methods: {
    dateFormat(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY");
    },
    async loadData(trackingNo) {
      this.loading = true;
      try {
        const [boRes, changesRes] = await Promise.allSettled([
          api().get(`/bo/forms/${trackingNo}/end-beneficial-owners?children=true`),
          api().get(`/bo/forms/${trackingNo}/beneficial-owner-form-changes`)
        ]);

        if (boRes.status === "fulfilled" && boRes.value.data?.data) {
          const boOwners = boRes.value.data.data.beneficialOwners || [];
          this.persons = boOwners.map(owner => this.mapOwnerToPerson(owner));
        }

        let apiChanges = null;
        if (changesRes.status === "fulfilled" && changesRes.value.data?.data) {
          apiChanges = changesRes.value.data.data;
        }

        this.processTaskDataChanges(this.taskData, apiChanges);
      } catch (error) {
        console.error("Failed to load beneficial owners data:", error);
      } finally {
        this.loading = false;
      }
    },

    mapOwnerToPerson(owner) {
      if (!owner) return {};
      const ind = owner.individual || owner;
      const nc = owner.natureOfControl || owner;
      return {
        givenName: ind.givenName || "",
        middleName: ind.middleName || "",
        surName: ind.surName || ind.name || "",
        idType: ind.idType || "Passport",
        idNumber: ind.idNumber || "",
        nationality: ind.nationality || (ind.country ? ind.country.name : ""),
        email: ind.email || "",
        phoneNumber: ind.phoneNumber || ind.phone || "",
        sex: ind.sex || "",
        dateOfBirth: ind.dateOfBirth || "",
        occupation: ind.occupation || "",
        village: ind.village || null,
        foreignPhysicalAddress: ind.foreignPhysicalAddress || ind.address || "",
        postalAddress: ind.postalAddress || ind.foreignPostalAddress || "",
        street: ind.street || "",
        plot: ind.plot || "",
        parentBrn: owner.parentBrn || "",
        parentName: owner.parentName || "",
        percentageShares: this.formatNatureOfControl(nc),
        percentageVotingRights: nc.percentageVotingRights || "",
        hasVotingRights: nc.hasVotingRights || "",
        hasRightToAppoint: nc.hasRightToAppoint || "",
        amountGuaranteed: nc.amountGuaranteed ? `UGX ${nc.amountGuaranteed.toLocaleString()}` : ""
      };
    },

    processTaskDataChanges(taskData, apiChanges = null) {
      const changesSrc = apiChanges || taskData?.changes || taskData?.form?.changes || taskData;

      if (changesSrc?.addedBeneficialOwners) {
        const addedList = changesSrc.addedBeneficialOwners.beneficialOwners || changesSrc.addedBeneficialOwners || [];
        this.addedOwners = (Array.isArray(addedList) ? addedList : []).map(o => this.mapOwnerToPerson(o));
      }

      if (changesSrc?.removedBeneficialOwners) {
        const removedList = changesSrc.removedBeneficialOwners.beneficialOwners || changesSrc.removedBeneficialOwners || [];
        this.removedOwners = (Array.isArray(removedList) ? removedList : []).map(o => this.mapOwnerToPerson(o));
      }

      this.changedList = this.buildChangedList(changesSrc);
    },

    buildChangedList(changes) {
      if (!changes) return [];
      if (Array.isArray(changes)) {
        return changes
          .map(c => ({
            name: c.name || "—",
            changes: (c.changes || [])
              .filter(chg => !this.isIgnoredChangeField(chg.type || chg.field))
              .map(chg => ({
                type: this.mapBoChangeType(chg.type || chg.field || ""),
                from: this.formatChangeDisplayValue(chg.type || chg.field, chg.from || chg.oldValue || "—"),
                to: this.formatChangeDisplayValue(chg.type || chg.field, chg.to || chg.newValue || "—")
              }))
          }))
          .filter(c => c.changes.length > 0);
      }

      const out = [];

      if (changes?.addressChange) {
        out.push({
          name: "Partnership address",
          changes: [
            {
              type: "Physical address",
              from: changes.addressChange.oldValue ?? "—",
              to: changes.addressChange.newValue ?? "—"
            }
          ]
        });
      }

      if (Array.isArray(changes?.formChanges)) {
        for (const fc of changes.formChanges) {
          if (this.isIgnoredChangeField(fc.field || fc.type)) continue;
          out.push({
            name: "Company detail",
            changes: [
              {
                type: this.mapBoChangeType(fc.field),
                from: fc.oldValue ?? "—",
                to: fc.newValue ?? "—"
              }
            ]
          });
        }
      }

      for (const bo of changes?.beneficialOwnerChanges ?? []) {
        if (!bo?.changes?.length) continue;
        const validChanges = bo.changes.filter((c) => !this.isIgnoredChangeField(c.type || c.field));
        if (!validChanges.length) continue;
        out.push({
          name: bo.name ?? "—",
          changes: validChanges.map((c) => ({
            type: this.mapBoChangeType(c.type || c.field),
            from: this.formatChangeDisplayValue(c.type || c.field, c.oldValue),
            to: this.formatChangeDisplayValue(c.type || c.field, c.newValue)
          }))
        });
      }

      for (const bo of changes?.changedBeneficialOwners ?? []) {
        const name = bo.current?.name || bo.previous?.name || "—";
        if (!bo?.changes?.length) continue;
        const validChanges = bo.changes.filter((c) => !this.isIgnoredChangeField(c.field || c.type));
        if (!validChanges.length) continue;
        out.push({
          name: name,
          changes: validChanges.map((c) => ({
            type: this.mapBoChangeType(c.field || c.type),
            from: this.formatChangeDisplayValue(c.field || c.type, c.oldValue),
            to: this.formatChangeDisplayValue(c.field || c.type, c.newValue)
          }))
        });
      }

      return out;
    },

    mapBoChangeType(type) {
      const u = (type || "").toUpperCase();
      if (u === "PERCENTAGE_SHARES") return "Percentage shares";
      if (u === "VOTING_RIGHTS") return "Voting rights";
      if (u === "DATE_OF_EFFECT") return "Date of effect";
      return String(type || "")
        .replace(/_/g, " ")
        .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    },

    formatChangeDisplayValue(changeType, value) {
      if (value == null || value === "") return "—";
      const t = (changeType || "").toUpperCase();
      if (t === "PERCENTAGE_SHARES" || t === "VOTING_RIGHTS") {
        const n = String(value).replace(/%/g, "").trim();
        return /^\d/.test(n) ? `${n}%` : String(value);
      }
      return String(value);
    },

    isIgnoredChangeField(field) {
      if (!field) return false;
      const f = String(field).trim().toLowerCase();
      return f === "numberofshares" || f === "number_of_shares" ||
             f === "relativepercentageshares" || f === "relative_percentage_shares";
    },

    formatNatureOfControl(nc) {
      let nature = "";
      if (!nc) return nature;
      if (nc.percentageShares != null && Number(nc.percentageShares) > 0) {
        nature += nc.percentageShares + "%";
      }
      return nature;
    },

    toggleShow(category, idx) {
      if (this.selectedCategory === category && this.selectedIdx === idx) {
        this.selectedIdx = null;
        this.showDetails = false;
      } else {
        this.selectedCategory = category;
        this.selectedIdx = idx;
        this.showDetails = true;
      }
    }
  }
};
</script>

<style scoped>
</style>
